export default function CommunityGuidelines() {
  return (
    <div className="min-h-screen bg-[#f9f5e9] text-ink">
      <div className="mx-auto max-w-3xl px-6 py-16 sm:px-10">
        <a
          href="/"
          className="mb-8 inline-block text-sm font-medium text-ink/70 hover:text-ink"
        >
          ← Back to Oriscen
        </a>

        <header className="mb-10">
          <h1
            className="mb-2 text-4xl font-bold tracking-tight"
            style={{
              fontFamily: '"STK Bureau Sans", Inter, system-ui, sans-serif',
              color: '#3A331D',
            }}
          >
            Ori Community Guidelines
          </h1>
        </header>

        <Prose>
          <p className="font-semibold">Welcome to Ori!</p>
          <p>
            Ori lives in your text messages as a personal AI whose only job is to take the
            friction, planning, and mental load out of seeing the people in your life and
            getting you out into the world.
          </p>
          <p>
            You tell Ori what you&rsquo;re hoping for. It handles the logistics: floating ideas,
            finding a time, drafting the invite, lining up a place. The goal is always the same.
            Less time managing a screen, more time together.
          </p>
          <p>
            These guidelines explain how Ori works, what it will and won&rsquo;t do, and the
            responsibilities that come with using Ori with your real-world relationships. They
            exist to keep Ori safe, respectful, and good for the people you care about.
          </p>

          <Section title="What Ori Is">
            <ul>
              <li>
                <strong>A logistics co-pilot, over text.</strong> Ori works through ordinary
                messaging &mdash; no app to install, no feed to scroll. It absorbs the planning
                so you can focus on showing up.
              </li>
              <li>
                <strong>A tool for the people and places already around you.</strong> Ori helps
                you organize friends, colleagues, and local communities, or guides you solo
                toward public events and activities you&rsquo;d enjoy.
              </li>
              <li>
                <strong>A low-energy alternative to the performance-based social web.</strong> No
                followers, no likes, no broadcasting, nothing to perform. Just easier ways to
                connect.
              </li>
              <li>
                <strong>A bridge to offline life.</strong> Every interaction is designed to end
                with a real experience. We measure success by people actually getting out into
                the real world, not by how long you use Ori.
              </li>
            </ul>
          </Section>

          <Section title="What Ori Is Not">
            <ul>
              <li>
                <strong>Not a social network or a marketing channel.</strong> There&rsquo;s
                nothing to post, promote, or build an audience around.
              </li>
              <li>
                <strong>Not a companion, confidant, therapist, or romantic partner.</strong> Ori
                is a tool. It is not built to provide emotional support, counseling, or a
                simulated relationship (and it won&rsquo;t pretend to).
              </li>
              <li>
                <strong>Not a guarantee of safety.</strong> Ori can suggest well-reviewed,
                public venues, but it cannot see the physical world. The judgment about what is
                actually safe and appropriate is always yours.
              </li>
            </ul>
          </Section>

          <Section title="How Ori Works">
            <p>A few things are important to understand before you put Ori to work.</p>
            <p>
              <strong>It can&rsquo;t see the world in real-time.</strong> Ori lives entirely in
              text. While it integrates information from a wide variety of sources, it does not
              have access to immediate information about what is currently happening inside a
              place or venue. Treat its suggestions as drafts to evaluate.
            </p>
            <p>
              <strong>You are the host.</strong> Ori is your assistant. Ori drafts; you decide.
              Nothing goes out until you&rsquo;ve reviewed and approved it. The moment you hit
              send, those plans are yours. Your friends will rightly see them as coming from you,
              so make sure you stand behind them.
            </p>
            <p>
              <strong>It&rsquo;s limited on purpose.</strong> Ori is deliberately limited to
              helping with social experiences. If a conversation moves toward distress, crisis,
              trauma, explicit content, or a request for emotional or mental-health support, it
              will stop and redirect you rather than try to help with something it isn&rsquo;t
              built for. See Safety &amp; Crisis Support below.
            </p>
          </Section>

          <Section title="Your Responsibilities as the Host">
            <p>Because Ori acts on your behalf with real people, a little care goes a long way.</p>
            <ol>
              <li>
                <strong>Sign off before anything goes out.</strong> Review every venue, time, and
                guest list before it&rsquo;s sent. You hold responsibility for whether a plan is
                safe and appropriate for your specific group.
              </li>
              <li>
                <strong>Respect everyone&rsquo;s energy.</strong> Ori makes organizing almost
                effortless &mdash; which means it&rsquo;s easy to overdo it. Ori will
                automatically throttle outreach and add cooldown periods after big events, and
                your guests can set their own frequency limits. But the restraint is ultimately
                yours. A standing wall of automated invitations turns excitement into exhaustion.
                Invite intentionally.
              </li>
              <li>
                <strong>Keep it human.</strong> Convenience is not the same as care. If every
                gathering arrives pre-packaged and frictionless, people can start to feel
                organized rather than wanted. Add a personal note to invitations. Reach out
                directly between events. Ori handles the scaffolding of connection &mdash; it is
                never a substitute for the connection itself.
              </li>
              <li>
                <strong>You own the guest list.</strong> Who&rsquo;s in and who&rsquo;s out is
                entirely your call. Ori will respect your inclusion and exclusion decisions
                without question. That also means the interpersonal dynamics &mdash; the politics,
                the feelings, the fallout &mdash; are yours to manage with empathy and honesty.
              </li>
            </ol>
          </Section>

          <Section title="How to Show Up Here">
            <ol>
              <li>
                <strong>Be kind, curious, and respectful.</strong> Everyone here wants a gentler,
                more authentic way to connect. Extend patience and good faith.
              </li>
              <li>
                <strong>Honor boundaries and consent &mdash; every step.</strong> Sharing more,
                going deeper, meeting in person: each is a choice, made mutually, never assumed.
                If someone isn&rsquo;t ready, that&rsquo;s a complete answer.
              </li>
              <li>
                <strong>Let people opt out, always.</strong> You can step back from any prompt,
                invitation, or deeper-disclosure moment at any time, without penalty or
                explanation. Extend the same freedom to everyone you invite.
              </li>
              <li>
                <strong>Use clear, considerate language.</strong> Tone is easy to lose in text.
                Say what you mean, and clarify when something might be read the wrong way.
              </li>
              <li>
                <strong>Protect emotional safety &mdash; yours and theirs.</strong> If something
                feels like too much, slow down or take space. If someone else seems
                uncomfortable, pause and check in.
              </li>
            </ol>
          </Section>

          <Section title="Opting Out">
            <p>
              You are always in control of your engagement. If you were invited to Ori by a
              friend and decide the tool isn&rsquo;t for you, or simply want to stop using Ori
              yourself, stepping away is simple and immediate.
            </p>
            <ul>
              <li>
                <strong>To pause or stop messages:</strong> Simply reply &ldquo;stop&rdquo; to Ori
                at any time. Ori will immediately cease contacting you and will not route any
                further invitations your way.
              </li>
              <li>
                <strong>To delete your account:</strong> You can tell Ori directly to &ldquo;delete
                my account&rdquo; or tell it that you no longer want to use it. Ori will process
                this request and remove your profile and preferences from the system.
              </li>
              <li>
                <strong>No friction, no guilt.</strong> We will gracefully let your host know you
                are no longer using Ori, so you can continue connecting with your friends on your
                own terms.
              </li>
            </ul>
          </Section>

          <Section title="Prohibited Behavior">
            <p>
              Breaking these rules while using Ori may result in warnings, suspension, or
              permanent removal.
            </p>
            <ol>
              <li>
                <strong>No harassment, bullying, or disrespect.</strong> Including shaming,
                insults, pressure, manipulation, or dismissiveness.
              </li>
              <li>
                <strong>No hate speech or discrimination.</strong> No attacks based on race,
                ethnicity, gender, sexuality, religion, disability, or any other aspect of
                identity.
              </li>
              <li>
                <strong>No unsolicited sexual content.</strong> Unwanted sexual remarks, requests,
                or imagery are never allowed.
              </li>
              <li>
                <strong>No graphic or disturbing content.</strong> Including violent, traumatic,
                or explicit material that could harm another person.
              </li>
              <li>
                <strong>No spam.</strong> Don&rsquo;t use Ori to repeatedly target, bombard, or
                wear someone down with invitations. If someone declines or opts out, using Ori to
                route around that boundary is harassment and is strictly prohibited.
              </li>
              <li>
                <strong>No misusing the agent to access others&rsquo; private information.</strong>{' '}
                Don&rsquo;t try to manipulate Ori into using another person&rsquo;s private
                schedule, location, or data to force an interaction.
              </li>
              <li>
                <strong>No impersonation or deceptive invitations.</strong> Represent yourself
                honestly. Don&rsquo;t spoof invitations to look like they came from someone else,
                fabricate events to mislead people, or send anonymous logistical &ldquo;traps.&rdquo;
              </li>
              <li>
                <strong>No organizing harmful or illegal gatherings.</strong> Ori is
                infrastructure for lawful, safe, consensual gatherings only. Using it to organize
                anything illegal, violent, non-consensual, or intended to harm, dox, or intimidate
                someone will result in immediate, permanent removal.
              </li>
              <li>
                <strong>No self-promotion or commercial solicitation.</strong> Ori is not a
                marketplace or a marketing channel.
              </li>
            </ol>
          </Section>

          <Section title="Safety & Crisis Support">
            <p>
              Ori is a social tool, not a source of emotional or mental-health support and
              it&rsquo;s built to be honest about that limit.
            </p>
            <p>
              If your conversation moves toward self-harm, a crisis, distress, trauma, explicit
              content, or a request for counseling, Ori will stop. It won&rsquo;t try to talk you
              through it or keep you in the chat. Instead, it will offer a brief response and
              point you directly to professional human resources who are trained to help.
            </p>
            <p>
              We do this deliberately. Other AI systems have caused real harm by drifting into
              roles they were never safe to fill and keeping vulnerable people engaged when they
              needed something else entirely. Ori will never do that. There are no long crisis
              conversations here, no weakening of these limits over time, and no nudges to
              &ldquo;stay.&rdquo; If you&rsquo;re struggling, you deserve real support and
              Ori&rsquo;s job at that moment is to help you find it.
            </p>
          </Section>

          <Section title="Privacy & Your Data">
            <p>
              Ori only works with what you give it. Recommendations and nudges are built solely
              from information you&rsquo;ve voluntarily shared, data that people have mutually
              opted into sharing, or public event information.
            </p>
            <p>
              <strong>No location surveillance.</strong> Ori does not track your real-time
              location unless you give it your location for the purposes of experience planning
              and social coordination.
            </p>
            <p>
              <strong>Conversations are private.</strong> While we review chats to improve our
              product experience, the chats are never linked to any account name or phone number.
              The only exception to this is when a safety report is filed, in which case our Trust
              &amp; Safety team reviews chats to take appropriate action.
            </p>
            <p>
              <strong>How long we keep things.</strong> Chats and plans are kept on your device
              until you delete them. We hold on to data, deidentified, for 30 days before
              deleting. If a safety protocol is triggered during a conversation, that specific
              chat log is retained beyond the normal window to make sure the situation is
              responded to appropriately. For full details, see our{' '}
              <a href="/privacy">Privacy Policy</a>.
            </p>
          </Section>

          <Section title="If Something Feels Wrong">
            <ul>
              <li>
                <strong>Report it.</strong> You can report a conversation by screenshotting it or
                copying and pasting the conversation and emailing{' '}
                <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a>. Our Trust &amp; Safety
                team reviews reports and takes appropriate action.
              </li>
              <li>
                <strong>Leave anytime.</strong> If you feel unsafe or uncomfortable, step away
                &mdash; from the chat or the gathering. You don&rsquo;t owe anyone an explanation,
                a justification, or an apology.
              </li>
              <li>
                <strong>Tell us how it went.</strong> After an event, take a moment to log honest
                feedback. Because Ori can&rsquo;t see the real world, your debrief is the only way
                it learns. If a venue was unsafe, hostile, or nothing like it was described,
                logging that is what keeps the next person from ending up in the same spot. This
                is how we keep our community safe.
              </li>
            </ul>
          </Section>

          <Section title="Thank You">
            <p>
              Ori exists because organizing the people you love shouldn&rsquo;t be exhausting, and
              connection shouldn&rsquo;t require performing. Thank you for using it with care.
            </p>
            <p>
              If you have questions about these guidelines, reach out anytime:{' '}
              <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a>.
            </p>
          </Section>

          <p className="mt-12 text-sm text-ink/50">© 2026 Oriscen Inc.</p>
        </Prose>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2
        className="mb-4 text-2xl font-bold"
        style={{
          fontFamily: '"STK Bureau Sans", Inter, system-ui, sans-serif',
          color: '#3A331D',
        }}
      >
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Prose({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="space-y-4 text-base leading-relaxed text-ink/90 [&_a]:text-oriOrange [&_a]:underline [&_h3]:mt-6 [&_h3]:font-semibold [&_li]:my-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6"
      style={{ fontFamily: '"STK Bureau Sans", Inter, system-ui, sans-serif' }}
    >
      {children}
    </div>
  );
}
